import React, { useState, useEffect } from 'react'
import { Modal, Button, Icon, Input, Select, message } from 'antd'
import { useWardrobe } from '../../../../hooks/useWardrobe'
import { OutfitGrid, useOutfitLibrary } from './shared'
import CreateOutfitModal from './CreateOutfitModal'
import WardrobeLibraryPickerModal from './WardrobeLibraryPickerModal'
import './WardrobeManagerModal.scss'

const { Option } = Select

/**
 * Modal for managing the global wardrobe library
 */
const WardrobeManagerModal = ({
  visible,
  onClose,
  formData,
  setFormData,
  videoId
}) => {
  const {
    deleteOutfit,
    createOutfit,
    isCreatingOutfit: isCreating,
    creationProgress: progress,
    stopPolling
  } = useWardrobe({ videoId })

  const {
    outfits: libraryOutfits,
    isLoading: isLibraryLoading,
    fetchIfNeeded,
    refresh,
    goToPage,
    pagination
  } = useOutfitLibrary({ formData, setFormData, videoId })

  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({ style: '', season: '' })
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [selectedOutfit, setSelectedOutfit] = useState(null)

  useEffect(() => {
    if (visible) {
      fetchIfNeeded()
    }
    return () => stopPolling()
  }, [visible])

  const handleSearch = () => {
    if (searchQuery.trim()) {
      refresh({ action: 'search', query: searchQuery })
    } else {
      refresh()
    }
  }

  const handleFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value || '' }
    setFilters(newFilters)
    refresh({ action: 'filter', ...newFilters })
  }

  const handleDelete = async outfit => {
    try {
      await deleteOutfit(outfit.tag || outfit.id)
      message.success('Outfit deleted')
      refresh()
    } catch (err) {
      message.error('Failed to delete outfit')
    }
  }

  const handleOutfitClick = outfit => {
    setSelectedOutfit(outfit)
  }

  return (
    <>
      <Modal
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={920}
        centered
        className="wardrobe-manager-modal"
        title={null}
        closable={false}
        destroyOnClose
      >
        <div className="manager-container">
          {/* Header */}
          <div className="manager-header">
            <h2 className="manager-title">Manage Wardrobe</h2>
            <div className="close-icon" onClick={onClose}>
              <Icon type="close" />
            </div>
          </div>

          {/* Toolbar */}
          <div className="manager-toolbar">
            <div className="search-section">
              <Input
                placeholder="Search outfits..."
                prefix={<Icon type="search" />}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </div>

            <div className="filter-section">
              <Select
                placeholder="All Styles"
                value={filters.style || undefined}
                onChange={val => handleFilter('style', val)}
                allowClear
                className="filter-select"
              >
                <Option value="formal">Formal</Option>
                <Option value="casual">Casual</Option>
                <Option value="sporty">Sporty</Option>
                <Option value="business">Business</Option>
              </Select>

              <Select
                placeholder="All Seasons"
                value={filters.season || undefined}
                onChange={val => handleFilter('season', val)}
                allowClear
                className="filter-select"
              >
                <Option value="summer">Summer</Option>
                <Option value="winter">Winter</Option>
                <Option value="spring">Spring</Option>
                <Option value="fall">Fall</Option>
              </Select>
            </div>
          </div>

          {/* Subheader */}
          <div className="manager-subheader">
            <div className="subheader-text">
              <h3>Wardrobe Library</h3>
              <p>Browse and manage your global collection of outfits.</p>
            </div>
            <div className="subheader-actions">
              <Button
                type="primary"
                icon="plus"
                onClick={() => setIsCreateModalVisible(true)}
              >
                Add New Outfit
              </Button>
              <Button
                icon="reload"
                onClick={() => refresh()}
                loading={isLibraryLoading}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Grid */}
          <div className="manager-content">
            <OutfitGrid
              outfits={libraryOutfits}
              onItemClick={handleOutfitClick}
              isLoading={isLibraryLoading}
              emptyMessage="No outfits found in your library. Add one to get started!"
              showDelete={true}
              onDelete={handleDelete}
              pagination={pagination}
              onPageChange={goToPage}
            />
          </div>
        </div>

        <CreateOutfitModal
          visible={isCreateModalVisible}
          onClose={() => setIsCreateModalVisible(false)}
          onSuccess={() => refresh()}
          createOutfit={createOutfit}
          isCreating={isCreating}
          creationProgress={progress}
        />
      </Modal>

      <WardrobeLibraryPickerModal
        visible={!!selectedOutfit}
        onClose={() => setSelectedOutfit(null)}
        initialOutfit={selectedOutfit}
        mode="view"
        formData={formData}
        setFormData={setFormData}
        videoId={videoId}
      />
    </>
  )
}

export default WardrobeManagerModal
